<template>
	<div>
		<h5 class="text-h5 q-mt-lg q-mb-md">{{ $t(game_data.game_name) }}</h5>

		<q-collapsible
			class="shadow-2 q-pa-sm q-ma-xs rounded-border"
			icon="far fa-ballot-check"
			right-icon="far fa-ballot-check"
			:label="$t('how_to_create_the_game')"
		>
			<div class="q-ma-sm">
				<p>{{ $t('find_the_pairs_instructions') }}</p>
				<p class="q-mt-lg">
					<em
						><strong>{{ `${$t('warning')}: ` }}</strong
						>{{ $t('create_game_before_save') }}</em
					>
				</p>
			</div>
		</q-collapsible>

		<div class="row">
			<div class="col-12 q-mb-md">
				<div class="row gutter-sm">
					<div class="col-12">
						<q-list
							v-if="game_data && game_data.words.length"
							dense
							bordered
							padding
							separator
							class="q-mt-md"
						>
							<q-item v-for="(word, index) in game_data.words" :key="index">
								<q-item-main class="q-mx-sm row">
									<q-field :count="140" class="col-5">
										<q-input
											v-model="word.question"
											type="text"
											:float-label="$t('question')"
											:error="$v.game_data.words.$each[index].question.$error"
											@input="$v.game_data.words.$each[index].question.$touch"
											@change="game_built ? resetGame() : null"
										/>
									</q-field>
									<q-field :count="140" class="col-5 offset-1">
										<q-input
											v-model="word.answer"
											type="text"
											:float-label="$t('correct_answer')"
											:error="$v.game_data.words.$each[index].answer.$error"
											@input="$v.game_data.words.$each[index].answer.$touch"
											@change="game_built ? resetGame() : null"
										/>
									</q-field>
								</q-item-main>
								<q-item-side right>
									<q-icon
										v-if="word.question.length && word.answer.length"
										name="fal fa-check"
										color="positive"
									/>
									<q-btn
										round
										flat
										v-if="game_data.words.length > 2"
										color="negative"
										icon="fal fa-trash"
										@click="removeWord(index)"
									>
										<q-tooltip>
											{{ $t('remove') }}
										</q-tooltip>
									</q-btn>
								</q-item-side>
							</q-item>
						</q-list>

						<div class="col-xs-12 col-sm-4 q-mt-sm q-mx-xs">
							<q-field :helper="$t('leave_it_blank_or_negative_to_use_infinite')">
								<q-input
									v-model="game_data.max_errors"
									type="number"
									:float-label="$t('alternative_error_limit')"
									@change="game_built ? resetGame() : null"
								/>
							</q-field>
						</div>

						<div class="row justify-end q-mr-md q-mt-md">
							<q-btn
								class="q-mr-md"
								color="primary"
								icon="far fa-plus"
								:label="$t('add_word')"
								@click="addWord"
								:disable="game_data.words.length > 9"
							/>
							<q-btn
								color="secondary"
								:label="$t('preview')"
								@click="buildGamePreview"
								:disable="disableGamePreview"
							/>
						</div>
					</div>
				</div>
			</div>
		</div>

		<find-the-pairs-preview
			v-if="show_game_preview"
			:show_game_preview="show_game_preview"
			@hide_preview="hidePreview()"
			:game_name="game_data.game_name"
			:game_words="game_data.words"
			:game_options="game_data.options"
			:game_max_errors="game_data.max_errors"
		/>
	</div>
</template>

<script>
import { required, maxLength, maxValue } from 'vuelidate/lib/validators'
import FindThePairsPreview from 'components/admin/games/FindThePairsPreview'

export default {
	name: 'AdminGameFindThePairs',

	props: ['can_save', 'saved_game_data', 'id_game'],

	components: {
		'find-the-pairs-preview': FindThePairsPreview,
	},

	data() {
		return {
			game_data: {
				game_name: 'find_the_pairs',
				max_errors: null,
				options: {
					answers: [],
					questions: [],
				},
				words: [
					{
						answer: '',
						question: '',
					},
					{
						answer: '',
						question: '',
					},
				],
			},
			show_game_preview: false,
			game_built: false,
		}
	},

	computed: {
		disableGamePreview() {
			if (this.game_data.words.length < 1) return true
			else {
				return this.game_data.words.findIndex(
					(e) => e.question === '' || e.answer === ''
				) !== -1
					? true
					: false
			}
		},
	},

	methods: {
		buildGamePreview() {
			this.game_data.options = {
				questions: this.game_data.words.map((w) => ({
					value: w.question,
					selected: false,
					position: 0,
					correct: undefined,
				})),
				answers: this.game_data.words.map((w) => ({
					value: w.answer,
					selected: false,
					position: 0,
					correct: undefined,
				})),
			}

			this.$v.$touch()
			if (!this.$v.$error) {
				this.show_game_preview = true
				this.game_built = true
				this.$emit('save_toggle', this.game_built)
				this.$emit('save_game_data', this.game_data)
			}
		},

		resetGame() {
			this.game_built = false
			this.$emit('save_toggle', this.game_built)
			this.$emit('save_game_data', null)
		},

		hidePreview() {
			this.show_game_preview = false
		},

		addWord() {
			const newSlot = {
				question: '',
				answer: '',
			}
			this.game_data.words.push(newSlot)
			if (this.game_built) {
				this.resetGame()
			}
		},

		removeWord(index) {
			this.game_data.words.splice(index, 1)
			if (this.game_built) {
				this.resetGame()
			}
		},
	},

	mounted() {
		if (this.saved_game_data) {
			this.game_data = this.saved_game_data
		} else {
			this.game_data = {
				game_name: 'find_the_pairs',
				max_errors: null,
				options: {
					answers: [],
					questions: [],
				},
				words: [
					{
						answer: '',
						question: '',
					},
					{
						answer: '',
						question: '',
					},
				],
			}
		}
	},

	beforeDestroy() {
		this.resetGame()
	},

	validations() {
		return {
			game_data: {
				words: {
					$each: {
						question: { required, maxLength: maxLength(140) },
						answer: { required, maxLength: maxLength(140) },
					},
				},
			},
		}
	},
}
</script>

<style></style>
